import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
    attachmentService,
    type AttachmentVO,
} from '@/services/auditService/attachment';
import type { RichTextUploadContext } from '@/pages/components/common/richText/types';
import {
    attachmentTargetTypeOf,
    canUpload,
    formatBytes,
    toUploadBo,
} from '@/pages/components/common/richText/htmlUtils';

export interface RichTextAttachmentPanelProps {
    uploadContext?: RichTextUploadContext;
    readOnly?: boolean;
    onAttachmentsChange?: (items: AttachmentVO[]) => void;
    onUploadError?: (message: string) => void;
}

export function RichTextAttachmentPanel({
    uploadContext,
    readOnly,
    onAttachmentsChange,
    onUploadError,
}: RichTextAttachmentPanelProps): React.ReactElement {
    const fileRef = useRef<HTMLInputElement>(null);
    const [items, setItems] = useState<AttachmentVO[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const emitError = useCallback((message: string) => {
        setError(message);
        onUploadError?.(message);
    }, [onUploadError]);

    const refresh = useCallback(async () => {
        if (!canUpload(uploadContext)) {
            setItems([]);
            onAttachmentsChange?.([]);
            return;
        }
        const type = attachmentTargetTypeOf(uploadContext);
        setLoading(true);
        try {
            const list = await attachmentService.listAttachments(type, uploadContext.targetId);
            setItems(list);
            onAttachmentsChange?.(list);
        } catch (e) {
            emitError(e instanceof Error ? e.message : 'Failed to load attachments');
        } finally {
            setLoading(false);
        }
    }, [uploadContext, onAttachmentsChange, emitError]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const handleAdd = async (files: File[]) => {
        if (!canUpload(uploadContext)) {
            emitError('Missing upload context or target is not saved yet. Save the record before adding attachments.');
            return;
        }
        if (files.length === 0) return;
        setUploading(true);
        setError(null);
        try {
            const type = attachmentTargetTypeOf(uploadContext);
            const bo = toUploadBo(uploadContext, type);
            const result = await attachmentService.batchUploadAttachments(files, bo);
            if (result.errors.length > 0) {
                emitError(result.errors.join('\n'));
            }
            await refresh();
        } catch (e) {
            emitError(e instanceof Error ? e.message : 'Attachment upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (att: AttachmentVO) => {
        if (!uploadContext?.companyId || !uploadContext?.annualId) {
            emitError('Missing company or annual information.');
            return;
        }
        try {
            await attachmentService.downloadAttachmentViaStream({
                companyId: uploadContext.companyId,
                annualId: uploadContext.annualId,
                fileKey: att.fileKey,
                fileName: att.fileName,
            });
        } catch (e) {
            emitError(e instanceof Error ? e.message : 'Download failed');
        }
    };

    const handleDelete = async (att: AttachmentVO) => {
        try {
            await attachmentService.deleteAttachment(att.id);
            await refresh();
        } catch (e) {
            emitError(e instanceof Error ? e.message : 'Delete failed');
        }
    };

    const uploadEnabled = canUpload(uploadContext) && !readOnly;

    return (
        <Box sx={{ px: 1.5, py: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Attachments
                </Typography>
                {!readOnly && (
                    <Button
                        size="small"
                        startIcon={uploading ? <CircularProgress size={14} /> : <AddOutlinedIcon />}
                        disabled={!uploadEnabled || uploading}
                        onClick={() => fileRef.current?.click()}
                    >
                        Add
                    </Button>
                )}
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 1, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {!canUpload(uploadContext) && !readOnly && (
                <Typography variant="caption" color="text.secondary">
                    Save the record first to enable attachments.
                </Typography>
            )}

            {loading ? (
                <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={20} />
                </Box>
            ) : items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                    No attachments yet
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>File</TableCell>
                            <TableCell width={90}>Size</TableCell>
                            <TableCell width={88} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((att) => (
                            <TableRow key={att.id}>
                                <TableCell>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                                        <Typography variant="body2" noWrap title={att.fileName}>
                                            {att.fileName}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">{formatBytes(att.fileSize)}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        aria-label="download"
                                        onClick={() => void handleDownload(att)}
                                    >
                                        <DownloadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    {!readOnly && (
                                        <IconButton
                                            size="small"
                                            aria-label="delete"
                                            onClick={() => void handleDelete(att)}
                                        >
                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                    const list = e.target.files;
                    if (list && list.length > 0) {
                        void handleAdd(Array.from(list));
                    }
                    e.target.value = '';
                }}
            />
        </Box>
    );
}
